import type {
  CompositionV3Filters,
  PostingsV3Filters,
  SentimentV3Filters,
  TransitionsV3Filters,
} from '@revelio/data-access';

import { type Filter, FilterType } from '../types/filters';

/**
 * The INDUSTRY keys this builds, as a value for the same reason the geography
 * ones are (`GEOGRAPHY_FILTER_KEYS`): callers need the list, not just the type.
 * `company` is deliberately absent — it is an entity, not an industry level.
 */
export const RICS_FILTER_KEYS = [
  'rics_k10',
  'rics_k50',
  'rics_k400',
] as const satisfies readonly (keyof CompositionV3Filters)[];

type RicsFilterKey = (typeof RICS_FILTER_KEYS)[number];

type CompanyFilterResponse =
  | Pick<CompositionV3Filters, RicsFilterKey | 'company'>
  | Pick<TransitionsV3Filters, RicsFilterKey | 'company'>
  | Pick<PostingsV3Filters, RicsFilterKey | 'company'>
  | Pick<SentimentV3Filters, RicsFilterKey | 'company'>;

export const getV3CompanyFilters = (filter: Filter): CompanyFilterResponse => {
  if (filter.type !== FilterType.TREE) return {};

  return filter.selections.reduce<CompanyFilterResponse>((acc, selection) => {
    const lastSegment = selection.id.split('_').slice(-1)[0] ?? '';
    const numericStr = lastSegment.includes('.')
      ? (lastSegment.split('.').pop() ?? lastSegment)
      : lastSegment;
    const selectionId = parseInt(numericStr, 10);

    if (!Number.isFinite(selectionId)) {
      return acc;
    }

    if (selection.levelId === 'rics_k10') {
      return { ...acc, rics_k10: [...(acc.rics_k10 ?? []), selectionId] };
    }
    if (selection.levelId === 'rics_k50') {
      return { ...acc, rics_k50: [...(acc.rics_k50 ?? []), selectionId] };
    }
    if (selection.levelId === 'rics_k400') {
      return { ...acc, rics_k400: [...(acc.rics_k400 ?? []), selectionId] };
    }
    if (selection.levelId === 'company') {
      return { ...acc, company: [...(acc.company ?? []), selectionId] };
    }

    return acc;
  }, {});
};
